import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Save, X, AlertTriangle, Loader2, Sparkles, RefreshCw } from 'lucide-react';
import { Card } from '../components/Card';
import { SectionHeader } from '../components/Tabs';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Field, Input } from '../components/Input';
import { Dialog, EmptyState, Skeleton, Spinner } from '../components/Dialog';
import { OperatorStat } from '../components/OperatorStat';
import { adminFetch } from '../adminApi';
import { formatNumber, formatUSD, cn } from '../lib/utils';

/**
 * PlansPage — manage the 5 plan tiers and their pricing/rate limits.
 *
 * Cards in a responsive grid. Click "Edit" to open a modal with all fields.
 * Above the grid, the yearly discount setting is surfaced so the operator
 * can change it without leaving the page.
 */
export default function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const data = await adminFetch('/admin/plans');
      setPlans(data.plans || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const total = plans.length;
  const visible = plans.filter((p) => p.visible).length;
  const cheapest = plans
    .filter((p) => p.monthly_price_cents > 0 && p.visible)
    .sort((a, b) => a.monthly_price_cents - b.monthly_price_cents)[0];
  const priciest = plans
    .filter((p) => p.visible)
    .sort((a, b) => b.monthly_price_cents - a.monthly_price_cents)[0];

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Plans"
        description="Manage pricing, rate limits, and credit refill amounts."
        actions={
          <Button variant="secondary" size="sm" onClick={load}>
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
        }
      />

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <OperatorStat
          icon={Edit3}
          label="Plan tiers"
          value={loading ? null : formatNumber(total)}
          hint={loading ? 'Loading…' : `${visible} public · ${total - visible} internal`}
          tone="neutral"
          loading={loading}
        />
        <OperatorStat
          icon={Sparkles}
          label="Cheapest paid"
          value={loading || !cheapest ? null : `${formatUSD(cheapest.monthly_price_cents)}/mo`}
          hint={loading ? 'Loading…' : cheapest?.display_name || cheapest?.code}
          tone="success"
          loading={loading}
        />
        <OperatorStat
          icon={Sparkles}
          label="Top tier"
          value={loading || !priciest ? null : `${formatUSD(priciest.monthly_price_cents)}/mo`}
          hint={loading ? 'Loading…' : priciest?.display_name || priciest?.code}
          tone="brand"
          loading={loading}
        />
        <OperatorStat
          icon={Sparkles}
          label="Discountable"
          value={loading ? null : formatNumber(visible - (plans.find((p) => p.code === 'free') ? 1 : 0))}
          hint={loading ? 'Loading…' : 'Eligible for yearly billing'}
          tone="neutral"
          loading={loading}
        />
      </div>

      <YearlyDiscountCard />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((plan) => (
            <PlanEditor
              key={plan.code}
              plan={plan}
              onEdit={() => setEditing(plan)}
            />
          ))}
        </div>
      )}

      <EditDialog
        plan={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

/* Yearly-discount card — surfaces the operator-level setting. */
function YearlyDiscountCard() {
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function load() {
    try {
      const s = await adminFetch('/admin/settings');
      setSettings(s);
      setDraft(String(s.yearly_discount_pct ?? ''));
    } catch { /* ignore */ }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    const pct = parseFloat(draft);
    if (isNaN(pct) || pct < 0 || pct > 50) {
      setErr('0–50% please');
      return;
    }
    setBusy(true); setErr(null);
    try {
      await adminFetch('/admin/settings', {
        method: 'PATCH',
        body: { yearly_discount_bps: Math.round(pct * 100) },
      });
      await load();
      setEditing(false);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <Card>
      <Card.Body>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-brand-softest text-brand-strong flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-fg">Yearly discount</div>
              <div className="text-xs text-fg-muted">
                Applied automatically when a customer picks yearly billing.
                Source: {settings?.source === 'db' ? 'database (overridden)' : 'default'}.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min={0}
                    max={50}
                    step={0.5}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="w-20 font-mono text-right"
                  />
                  <span className="text-sm text-fg-muted">%</span>
                </div>
                <Button size="sm" variant="primary" onClick={save} disabled={busy}>
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setErr(null); }}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            ) : (
              <>
                <span className="text-3xl font-semibold text-fg tabular-nums">
                  {settings ? `${settings.yearly_discount_pct}%` : '—'}
                </span>
                <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                  Edit
                </Button>
              </>
            )}
          </div>
        </div>
        {err && (
          <div className="mt-2 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5" /> {err}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function PlanEditor({ plan, onEdit }) {
  const isFree = plan.code === 'free';
  return (
    <Card>
      <Card.Header>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Card.Title>{plan.display_name || plan.code}</Card.Title>
            {!plan.visible && <Badge variant="warning">Hidden</Badge>}
            {plan.code === 'free' && <Badge variant="default">Default</Badge>}
          </div>
          <code className="text-2xs text-fg-muted font-mono mt-1 block">{plan.code}</code>
        </div>
      </Card.Header>
      <Card.Body className="space-y-3 pt-0">
        <div>
          <div className="text-2xl font-semibold text-fg tabular-nums">
            {isFree ? 'Free' : formatUSD(plan.monthly_price_cents)}
            {!isFree && <span className="text-xs text-fg-muted font-normal">/mo</span>}
          </div>
          <div className="text-2xs text-fg-muted mt-0.5">
            {formatNumber(plan.monthly_credit_refill)} credits/mo · {plan.rate_limit_per_minute}/min
          </div>
        </div>

        <div className="space-y-1.5 text-xs">
          <Row label="Per credit" value={plan.per_credit_cents ? `$${(plan.per_credit_cents/100).toFixed(2)}` : '—'} />
          <Row label="Hard block" value={plan.hard_block ? 'Yes' : 'No'} />
          <Row label="Visibility" value={plan.visible ? 'Public' : 'Internal'} />
          <Row label="Sort order" value={plan.sort_order ?? '—'} />
        </div>

        <Button variant="secondary" size="sm" className="w-full" onClick={onEdit}>
          <Edit3 className="h-3.5 w-3.5" />
          Edit
        </Button>
      </Card.Body>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-fg-muted">{label}</span>
      <span className="text-fg">{value}</span>
    </div>
  );
}

function EditDialog({ plan, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (plan) {
      setForm({
        display_name: plan.display_name,
        description: plan.description || '',
        monthly_price_cents: plan.monthly_price_cents,
        monthly_credit_refill: plan.monthly_credit_refill,
        rate_limit_per_minute: plan.rate_limit_per_minute,
        per_credit_cents: plan.per_credit_cents,
        hard_block: plan.hard_block,
        visible: plan.visible,
      });
      setError(null);
    } else {
      setForm(null);
    }
  }, [plan]);

  // Don't render anything until the form is initialized.
  // (Prevents "Cannot read properties of null" errors on first paint.)
  if (!plan || !form) return null;

  async function save() {
    setBusy(true); setError(null);
    try {
      await adminFetch(`/admin/plans/${plan.code}`, {
        method: 'PATCH',
        body: form,
      });
      onSaved?.();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  return (
    <Dialog
      open={!!plan}
      onClose={onClose}
      title={`Edit plan: ${plan.code}`}
      description="Changes apply to new keys immediately. Existing keys keep their snapshot."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3.5 w-3.5" /> Save</>}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <Field label="Display name" htmlFor="name">
          <Input id="name" value={form.display_name || ''} onChange={(e) => update('display_name', e.target.value)} />
        </Field>
        <Field label="Description" htmlFor="desc">
          <Input id="desc" value={form.description || ''} onChange={(e) => update('description', e.target.value)} />
        </Field>
        <Field label="Monthly price (cents)" htmlFor="price">
          <Input id="price" type="number" min="0" value={form.monthly_price_cents} onChange={(e) => update('monthly_price_cents', Number(e.target.value))} />
        </Field>
        <Field label="Monthly credit refill" htmlFor="refill">
          <Input id="refill" type="number" min="0" value={form.monthly_credit_refill} onChange={(e) => update('monthly_credit_refill', Number(e.target.value))} />
        </Field>
        <Field label="Rate limit (per minute)" htmlFor="rpm">
          <Input id="rpm" type="number" min="1" value={form.rate_limit_per_minute} onChange={(e) => update('rate_limit_per_minute', Number(e.target.value))} />
        </Field>
        <Field label="Per credit (cents)" htmlFor="pcc">
          <Input id="pcc" type="number" min="0" value={form.per_credit_cents} onChange={(e) => update('per_credit_cents', Number(e.target.value))} />
        </Field>
        <Field label="Hard block on overage" htmlFor="hb">
          <button
            type="button"
            role="switch"
            aria-checked={!!form.hard_block}
            onClick={() => update('hard_block', !form.hard_block)}
            className={cn(
              'relative h-5 w-9 shrink-0 rounded-full transition-colors',
              form.hard_block ? 'bg-brand' : 'bg-surface-3'
            )}
          >
            <span
              aria-hidden="true"
              className="absolute top-0.5 h-4 w-4 rounded-full bg-offwhite shadow transition-[left] duration-200 ease-out"
              style={{ left: form.hard_block ? '18px' : '2px' }}
            />
          </button>
        </Field>
        <Field label="Visible to customers" htmlFor="vis">
          <button
            type="button"
            role="switch"
            aria-checked={!!form.visible}
            onClick={() => update('visible', !form.visible)}
            className={cn(
              'relative h-5 w-9 shrink-0 rounded-full transition-colors',
              form.visible ? 'bg-brand' : 'bg-surface-3'
            )}
          >
            <span
              aria-hidden="true"
              className="absolute top-0.5 h-4 w-4 rounded-full bg-offwhite shadow transition-[left] duration-200 ease-out"
              style={{ left: form.visible ? '18px' : '2px' }}
            />
          </button>
        </Field>
      </div>

      {error && (
        <div className="mt-4 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">
          {error}
        </div>
      )}
    </Dialog>
  );
}